import React, { useState, useEffect } from "react";
import { SiteWide } from "./customComponents";
import Section from "./layouts/Section";
import { useNavigate } from "react-router-dom";
import APIRequests from "../utility/APIRequests";

// Material UI Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BoltIcon from "@mui/icons-material/Bolt";
import StarIcon from "@mui/icons-material/Star";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import NotificationsIcon from "@mui/icons-material/Notifications";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

function Notification() {
    const [notifications, setNotifications] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchNotifications = async (targetPage) => {
        setLoading(true);
        try {
            const api = new APIRequests();
            const res = await api.runGet(`notifications/load?page=${targetPage}&limit=10`);
            if (res && res.code === 1 && res.data) {
                setNotifications(res.data.list || []);
                setTotalPages(res.data.totalPages || 1);
                setTotalResults(res.data.totalResults || 0);
                setPage(res.data.page || 1);
            }
        } catch (err) {
            console.error("Error loading notifications:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications(page);
    }, [page]);

    const getNotificationStyle = (msg = "") => {
        const lower = msg.toLowerCase();
        if (lower.includes("referral") || lower.includes("joined")) {
            return {
                icon: <BoltIcon style={{ fontSize: 18 }} />,
                color: "#3b82f6",
                title: "Referral Network"
            };
        } else if (lower.includes("reward") || lower.includes("profit") || lower.includes("earned") || lower.includes("capital")) {
            return {
                icon: <MonetizationOnIcon style={{ fontSize: 18 }} />,
                color: "#22c55e",
                title: "Earning Payout"
            };
        } else if (lower.includes("withdrawal") || lower.includes("paid") || lower.includes("confirmed")) {
            return {
                icon: <ArrowDownwardIcon style={{ fontSize: 18 }} />,
                color: "#a855f7",
                title: "Withdrawal Confirmation"
            };
        } else if (lower.includes("completed")) {
            return {
                icon: <StarIcon style={{ fontSize: 18 }} />,
                color: "#f59e0b",
                title: "Session Completed"
            };
        } else {
            return {
                icon: <NotificationsIcon style={{ fontSize: 18 }} />,
                color: "#64748b",
                title: "Notification"
            };
        }
    };

    const formatRelativeTime = (timestamp) => {
        if (!timestamp) return "";
        const now = Math.floor(Date.now() / 1000);
        const diffSec = now - Number(timestamp);
        const diffMin = Math.floor(diffSec / 60);
        const diffHr = Math.floor(diffMin / 60);
        const diffDays = Math.floor(diffHr / 24);

        if (diffSec < 60) return "Just now";
        if (diffMin < 60) return `${diffMin}m ago`;
        if (diffHr < 24) return `${diffHr}h ago`;
        if (diffDays === 1) return "Yesterday";
        if (diffDays < 7) return `${diffDays}d ago`;

        const date = new Date(Number(timestamp) * 1000);
        return date.toLocaleDateString(undefined, { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Helper to generate elegant page numbers
    const getPageNumbers = () => {
        const pages = [];
        const start = Math.max(1, page - 2);
        const end = Math.min(totalPages, page + 2);
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <SiteWide title={"Notifications"}>
            <Section hideHeader={true} overlayText={""} loading={false} title={'Notifications'}>
                <div className="flex flex-col gap-4 pb-8 max-w-2xl mx-auto px-2">
                    
                    {/* Header bar */}
                    <div className="flex items-center gap-3 py-2">
                        <button 
                            onClick={() => navigate('/')}
                            className="flex items-center justify-center rounded-full transition-all active:scale-95 hover:bg-slate-100"
                            style={{ 
                                width: 36, 
                                height: 36, 
                                background: "#ffffff", 
                                border: "1px solid rgba(59,130,246,0.08)", 
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                                color: "#334155",
                                cursor: "pointer"
                            }}
                        >
                            <ArrowBackIcon style={{ fontSize: 18 }} />
                        </button>
                        <div>
                            <p style={{ color: "#64748b", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: 0, textTransform: "uppercase", letterSpacing: "0.06em" }}>Recent History</p>
                            <h2 style={{ color: "#0f172a", fontSize: 18, fontWeight: 700, fontFamily: "'Syne',sans-serif", lineHeight: 1.2, margin: 0, marginTop: 1 }}>
                                Activity Inbox
                            </h2>
                        </div>
                    </div>

                    {/* Feed Container */}
                    <div className="rounded-2xl p-5 relative overflow-hidden" style={{ background: "#ffffff", border: "1px solid rgba(59,130,246,0.08)", boxShadow: "0 4px 20px rgba(59,130,246,0.04)" }}>
                        {loading ? (
                            // Loading state
                            <div className="flex flex-col gap-4 py-8 items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <p style={{ color: "#64748b", fontSize: 13, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>Syncing feed...</p>
                            </div>
                        ) : notifications.length === 0 ? (
                            // Empty State
                            <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
                                <div className="rounded-2xl flex items-center justify-center" style={{ width: 64, height: 64, background: "rgba(59,130,246,0.04)", border: "1.5px solid rgba(59,130,246,0.08)", color: "#3b82f6" }}>
                                    <NotificationsIcon style={{ fontSize: 28 }} />
                                </div>
                                <div>
                                    <h4 style={{ color: "#0f172a", fontSize: 14, fontWeight: 700, fontFamily: "'Syne',sans-serif", margin: 0 }}>All caught up!</h4>
                                    <p style={{ color: "#64748b", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0, marginTop: 4, maxWidth: 220 }}>Your recent account transactions and chat rewards will appear here.</p>
                                </div>
                            </div>
                        ) : (
                            // Dynamic Paginated List
                            <div className="flex flex-col gap-3.5">
                                {notifications.map((n, i) => {
                                    const style = getNotificationStyle(n.message);
                                    return (
                                        <div 
                                            key={n.id || i} 
                                            className="flex items-start gap-3.5 p-3 rounded-xl transition-all"
                                            style={{ 
                                                background: "rgba(248,250,252,0.6)", 
                                                border: "1px solid rgba(226,232,240,0.4)",
                                                cursor: "default"
                                            }}
                                            onMouseEnter={e => {
                                                e.currentTarget.style.background = "#ffffff";
                                                e.currentTarget.style.borderColor = "rgba(59,130,246,0.15)";
                                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(59,130,246,0.04)";
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.background = "rgba(248,250,252,0.6)";
                                                e.currentTarget.style.borderColor = "rgba(226,232,240,0.4)";
                                                e.currentTarget.style.boxShadow = "none";
                                            }}
                                        >
                                            {/* Icon block */}
                                            <div 
                                                className="rounded-xl flex items-center justify-center shrink-0" 
                                                style={{ 
                                                    width: 40, 
                                                    height: 40, 
                                                    background: `${style.color}0a`, 
                                                    border: `1px solid ${style.color}1c`, 
                                                    color: style.color 
                                                }}
                                            >
                                                {style.icon}
                                            </div>

                                            {/* Text block */}
                                            <div className="flex-1 min-w-0">
                                                <p style={{ color: "#1e293b", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", margin: 0, lineHeight: 1.35 }}>
                                                    {n.message}
                                                </p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span style={{ color: style.color, fontSize: 10, fontWeight: 700, fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.02em" }}>
                                                        {style.title}
                                                    </span>
                                                    {n.ip && (
                                                        <>
                                                            <span style={{ color: "#cbd5e1", fontSize: 10 }}>•</span>
                                                            <span style={{ color: "#94a3b8", fontSize: 10, fontFamily: "'DM Sans',sans-serif" }}>
                                                                IP: {n.ip}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Time block */}
                                            <span style={{ color: "#94a3b8", fontSize: 10, fontFamily: "'DM Sans',sans-serif", whiteSpace: "nowrap", paddingTop: 2 }}>
                                                {formatRelativeTime(n.timestamp)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Pagination control block */}
                        {!loading && totalPages > 1 && (
                            <div className="flex items-center justify-between border-t border-slate-100 mt-6 pt-4">
                                <span style={{ color: "#64748b", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                                    Showing page {page} of {totalPages} ({totalResults} activities)
                                </span>
                                
                                <div className="flex items-center gap-1">
                                    {/* Previous button */}
                                    <button 
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        style={{ 
                                            background: page === 1 ? "transparent" : "#ffffff",
                                            border: "1px solid rgba(226,232,240,0.8)",
                                            color: page === 1 ? "#cbd5e1" : "#475569",
                                            cursor: page === 1 ? "not-allowed" : "pointer"
                                        }}
                                        className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:bg-slate-50"
                                    >
                                        <KeyboardArrowLeftIcon style={{ fontSize: 18 }} />
                                    </button>

                                    {/* Page numbers */}
                                    {getPageNumbers().map(num => (
                                        <button 
                                            key={num}
                                            onClick={() => setPage(num)}
                                            style={{ 
                                                background: num === page ? "#2563eb" : "#ffffff",
                                                border: num === page ? "1px solid #2563eb" : "1px solid rgba(226,232,240,0.8)",
                                                color: num === page ? "#ffffff" : "#475569",
                                                cursor: "pointer",
                                                fontWeight: num === page ? 700 : 500
                                            }}
                                            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
                                        >
                                            {num}
                                        </button>
                                    ))}

                                    {/* Next button */}
                                    <button 
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        style={{ 
                                            background: page === totalPages ? "transparent" : "#ffffff",
                                            border: "1px solid rgba(226,232,240,0.8)",
                                            color: page === totalPages ? "#cbd5e1" : "#475569",
                                            cursor: page === totalPages ? "not-allowed" : "pointer"
                                        }}
                                        className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:bg-slate-50"
                                    >
                                        <KeyboardArrowRightIcon style={{ fontSize: 18 }} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </Section>
        </SiteWide>
    );
}

export default Notification;